import { createPortal } from 'react-dom'

interface InvoiceDetailsData {
  number: string
  ref: string
  document: string
  date: string
  dueDate: string
  paid: string
  due: string
  status: string
  xeroInvoiceId?: string | null
}

interface InvoiceDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  invoiceData?: InvoiceDetailsData
}

const InvoiceDetailsModal = ({ isOpen, onClose, invoiceData }: InvoiceDetailsModalProps) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Paid':
        return { textColor: '#13AB5B', bgColor: '#E8F7EF' }
      case 'Pending':
        return { textColor: '#E93323', bgColor: '#FEE2E2' }
      case 'Overdue':
        return { textColor: '#E93323', bgColor: '#FEE2E2' }
      default:
        return { textColor: '#69697A', bgColor: '#F2F4F2' }
    }
  }

  if (!isOpen || !invoiceData) return null

  const statusStyle = getStatusColor(invoiceData.status)

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-end"
      onClick={onClose}
      style={{ zIndex: 60 }}
    >
      <div 
        className="bg-white shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{
          fontFamily: 'Lexend Deca',
          width: '450px',
          maxWidth: '450px',
          height: '100vh',
          zIndex: 70,
          borderTopLeftRadius: '12px',
          borderBottomLeftRadius: '12px'
        }}
      >
        {/* Header */}
        <div 
          className="flex items-center justify-between border-b border-gray-200 flex-shrink-0"
          style={{
            width: '100%',
            height: '74px',
            backgroundColor: '#F2F4F2',
            borderTopLeftRadius: '12px',
            paddingLeft: '24px',
            paddingRight: '24px'
          }}
        >
          <h2 
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 600,
              fontStyle: 'normal',
              fontSize: '18px',
              lineHeight: '26px',
              letterSpacing: '0px',
              color: '#212126'
            }}
          >
            Invoice Details
          </h2>
          <button
            onClick={onClose}
            className="flex items-center justify-center hover:opacity-80 transition-opacity"
            style={{ 
              width: '24px',
              height: '24px'
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* Outer circle */}
              <circle
                cx="12"
                cy="12"
                r="10.5"
                stroke="#212126"
                strokeWidth="1.5"
                fill="none"
              />
              {/* Inner X - top-left to bottom-right */}
              <line
                x1="9.17"
                y1="9.17"
                x2="14.83"
                y2="14.83"
                stroke="#212126"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              {/* Inner X - top-right to bottom-left */}
              <line
                x1="14.83"
                y1="9.17"
                x2="9.17"
                y2="14.83"
                stroke="#212126"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Details Content */}
        <div className="p-6 overflow-y-auto flex-1">
          <div className="space-y-4">
            {/* Number */}
            <div className="flex items-center py-3 border-b border-gray-100" style={{ gap: '40px' }}>
              <span 
                className="flex-shrink-0"
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 300,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A',
                  width: '140px'
                }}
              >
                Number
              </span>
              <span 
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 600,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#39553A'
                }}
              >
                {invoiceData.number}
              </span>
            </div>

            {/* Ref */}
            <div className="flex items-center py-3 border-b border-gray-100" style={{ gap: '40px' }}>
              <span 
                className="flex-shrink-0"
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 300,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A',
                  width: '140px'
                }}
              >
                Ref
              </span>
              <span 
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 500,
                  fontStyle: 'normal',
                  fontSize: '12px',
                  lineHeight: '20px',
                  letterSpacing: '0px',
                  color: '#212126'
                }}
              >
                {invoiceData.ref}
              </span>
            </div>

            {/* Document */}
            <div className="flex items-center py-3 border-b border-gray-100" style={{ gap: '40px' }}>
              <span 
                className="flex-shrink-0"
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 300,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A',
                  width: '140px'
                }}
              >
                Document
              </span>
              <div className="flex items-center gap-2">
                <img 
                  src="/images/clearing-status.svg" 
                  alt="Document" 
                  style={{ width: '16px', height: '16px' }}
                />
                <span 
                  style={{ 
                    fontFamily: 'Lexend Deca',
                    fontWeight: 500,
                    fontStyle: 'normal',
                    fontSize: '12px',
                    lineHeight: '20px',
                    letterSpacing: '0px',
                    color: '#212126'
                  }}
                >
                  {invoiceData.document}
                </span>
              </div>
            </div>

            {/* Date */}
            <div className="flex items-center py-3 border-b border-gray-100" style={{ gap: '40px' }}>
              <span 
                className="flex-shrink-0"
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 300,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A',
                  width: '140px'
                }}
              >
                Date
              </span>
              <span 
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 500,
                  fontStyle: 'normal',
                  fontSize: '12px',
                  lineHeight: '20px',
                  letterSpacing: '0px',
                  color: '#212126'
                }}
              >
                {invoiceData.date}
              </span>
            </div>

            {/* Due date */}
            <div className="flex items-center py-3 border-b border-gray-100" style={{ gap: '40px' }}>
              <span 
                className="flex-shrink-0"
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 300,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A',
                  width: '140px'
                }}
              >
                Due date
              </span>
              <span 
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 500,
                  fontStyle: 'normal',
                  fontSize: '12px',
                  lineHeight: '20px',
                  letterSpacing: '0px',
                  color: '#212126'
                }}
              >
                {invoiceData.dueDate}
              </span>
            </div>

            {/* Paid */}
            <div className="flex items-center py-3 border-b border-gray-100" style={{ gap: '40px' }}>
              <span 
                className="flex-shrink-0"
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 300,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A',
                  width: '140px'
                }}
              >
                Paid
              </span>
              <span 
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 500,
                  fontStyle: 'normal',
                  fontSize: '12px',
                  lineHeight: '20px',
                  letterSpacing: '0px',
                  color: '#212126'
                }}
              >
                {invoiceData.paid}
              </span>
            </div>

            {/* Due */}
            <div className="flex items-center py-3 border-b border-gray-100" style={{ gap: '40px' }}>
              <span 
                className="flex-shrink-0"
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 300,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A',
                  width: '140px'
                }}
              >
                Due
              </span>
              <span 
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 500,
                  fontStyle: 'normal',
                  fontSize: '12px',
                  lineHeight: '20px',
                  letterSpacing: '0px',
                  color: '#212126'
                }}
              >
                {invoiceData.due}
              </span>
            </div>

            {/* Status */}
            <div className="flex items-center py-3" style={{ gap: '40px' }}>
              <span 
                className="flex-shrink-0"
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 300,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A',
                  width: '140px'
                }}
              >
                Status
              </span>
              <span
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontStyle: 'normal',
                  fontSize: '12px',
                  lineHeight: '20px',
                  letterSpacing: '0px',
                  color: statusStyle.textColor,
                  backgroundColor: statusStyle.bgColor,
                  padding: '4px 8px',
                  borderRadius: '4px',
                  display: 'inline-block',
                }}
              >
                {invoiceData.status}
              </span>
            </div>
          </div>
        </div>

        {/* Bottom Action Bar */}
        <div 
          className="flex items-center justify-end gap-4 flex-shrink-0 border-t border-gray-200"
          style={{
            width: '100%',
            height: '74px',
            backgroundColor: '#F2F4F2',
            borderBottomLeftRadius: '12px',
            paddingLeft: '24px',
            paddingRight: '24px'
          }}
        >
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '22px',
              color: '#212126',
              backgroundColor: '#FFFFFF',
              border: '1px solid #E9E9EA',
              borderRadius: '8px'
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              const xeroId = invoiceData.xeroInvoiceId ?? (invoiceData as { _raw?: { xeroInvoiceId?: string | null } })._raw?.xeroInvoiceId
              if (xeroId) {
                const url = `https://go.xero.com/AccountsReceivable/View.aspx?InvoiceID=${encodeURIComponent(xeroId)}`
                window.open(url, '_blank', 'noopener,noreferrer')
              } else {
                window.open('https://go.xero.com/AccountsReceivable/', '_blank', 'noopener,noreferrer')
              }
            }}
            className="px-6 py-2 rounded-lg hover:opacity-90 transition-colors"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '22px',
              color: '#FFFFFF',
              backgroundColor: '#212126',
              borderRadius: '8px'
            }}
          >
            Track Externally
          </button>
        </div>
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}

export default InvoiceDetailsModal

